import { type AdditionalAttributes } from '../types';

import { getLogger } from '@/modules/observability/logging';

const PRINTABLE_ASCII_PATTERN = /^[\x20-\x7E]*$/;
const MAX_LOGGED_VALUE_LENGTH = 100;
const DROP_MESSAGE =
  '[JIT Bucketing] Dropped additional attribute from the bucketing call';

function truncateForLog(value: string): string {
  return value.length > MAX_LOGGED_VALUE_LENGTH
    ? `${value.slice(0, MAX_LOGGED_VALUE_LENGTH)}…`
    : value;
}

/**
 * Returns a reason string if the pair must be dropped, or null if it's safe
 * to emit on the wire.
 */
function findDropReason(
  key: string,
  value: AdditionalAttributes[string],
  serialisedValue: string,
  seenKeys: Set<string>
): string | null {
  if (!key) {
    return 'key is empty';
  }

  if (seenKeys.has(key)) {
    return 'key has already been emitted (reserved from_pla or a duplicate attribute key)';
  }

  if (!serialisedValue) {
    return 'value is empty';
  }

  /**
   * `NaN` and `Infinity` are legal `number`s, so a caller doing
   * `{ listing_count: Number(maybeBad) }` type-checks and would put the literal
   * strings `NaN`/`Infinity` on the wire, where Optimizely reads them as
   * ordinary attribute values that quietly never match a numeric audience.
   * Same for a nullish value arriving from an `as`-cast API response.
   */
  if (value === null || value === undefined) {
    return 'value is null or undefined';
  }

  if (typeof value === 'number' && !Number.isFinite(value)) {
    return 'value is a non-finite number (NaN or Infinity)';
  }

  if (key.includes(',') || serialisedValue.includes(',')) {
    return 'key or value contains a comma';
  }

  if (
    !PRINTABLE_ASCII_PATTERN.test(key) ||
    !PRINTABLE_ASCII_PATTERN.test(serialisedValue)
  ) {
    return 'key or value contains a character outside printable ASCII';
  }

  return null;
}

/**
 * Builds the Depop-Additional-Attribute header value: a flat, comma-separated
 * list of key,value pairs which the backend forwards to Optimizely as user
 * attributes, e.g. `from_pla,true,seller_tier,gold`.
 *
 * `from_pla` is always the first pair, so callers that pass no additional
 * attributes get exactly the header we have always sent.
 *
 * The key/value separator and the pair separator are both `,` and there is no
 * escaping, so a key or value containing a comma would shift every following
 * pair and corrupt unrelated attributes. An empty key or value produces the
 * same kind of dangling/misaligned separator. A key that has already been
 * emitted - including the reserved `from_pla` key - would silently redefine
 * an earlier pair depending on backend precedence. A nullish or non-finite
 * value would reach Optimizely as the literal text `null`/`NaN`/`Infinity`.
 * All of these are dropped and logged rather than sent.
 *
 * Header values are typed `string`, but `Headers.set` throws a `TypeError`
 * for some legal `string` values, e.g. a value containing a newline or a
 * character above U+00FF. Rather than allow-list exactly what `Headers.set`
 * accepts, keys and values are restricted here to printable ASCII
 * (`\x20`-`\x7E`): Optimizely attribute keys and values are ASCII in
 * practice, so a non-ASCII value is far more likely a mistake than intent -
 * and dropping one attribute with a loud log is strictly better than
 * throwing away the entire bucketing call because one value wasn't ASCII.
 * This deliberately also rejects Latin-1 values that `Headers.set` would
 * happily accept (e.g. `Malmö`); that's the intended trade-off.
 */
export function buildAdditionalAttributeHeader({
  isPLA,
  additionalAttributes,
}: {
  isPLA: boolean;
  additionalAttributes?: AdditionalAttributes;
}): string {
  const pairs = [`from_pla,${isPLA}`];
  const seenKeys = new Set(['from_pla']);
  const suppliedKeys = Object.keys(additionalAttributes ?? {});

  suppliedKeys.forEach((key) => {
    const value = (additionalAttributes as AdditionalAttributes)[key];
    const serialisedValue = String(value);

    const dropReason = findDropReason(key, value, serialisedValue, seenKeys);

    if (dropReason) {
      getLogger().error(DROP_MESSAGE, {
        key,
        reason: dropReason,
        value: truncateForLog(serialisedValue),
        suppliedKeys,
      });
      return;
    }

    seenKeys.add(key);
    pairs.push(`${key},${serialisedValue}`);
  });

  return pairs.join(',');
}
